// src/lib/acceptedDocuments.js
//
// THE single source of truth for which documents Companies House accepts for
// identity verification, transcribed from the official standard:
// https://www.gov.uk/guidance/how-to-meet-companies-house-identity-verification-standard
// (Companies House last updated 19 June 2026.)
//
// Why this file exists: the lists were maintained by hand in six places — the
// accepted-documents page, the glossary, a learn article, an email template,
// the assistant knowledge and Charlotte's knowledge base — and all six had
// drifted from each other and from the official standard. The drift cost
// customers in both directions:
//
//   - eVisa and insurance policy were missing entirely, so valid customers
//     were told we could not verify them.
//   - "Expired documents cannot be accepted" was stated flatly, when the
//     standard allows a passport up to 6 or 18 months expired depending on the
//     option. We do NOT use that tolerance — see FIRM POLICY below.
//   - The option to bring TWO Group A documents was never mentioned.
//   - The rule that a non-UK resident must supply at least one
//     government-issued document appeared nowhere.
//   - The glossary said "if your ID is on PRADO we can usually accept it",
//     which is true only for the person-checked option — the distinction a
//     Turkish director needed, and did not get, before asking us to confirm
//     his documents in writing.
//
// THE TWO OPTIONS ARE NOT INTERCHANGEABLE, and that is the trap. A photocard
// driving licence must be UK/CI/IoM/EU on BOTH options; the PRADO catch-all
// exists ONLY on the person-checked option. So a Turkish driving licence is
// valid by video call and invalid on the automated digital check — the opposite
// of the usual assumption that the "more thorough" route is the stricter one.

// ---------------------------------------------------------------------------
// OPTION 1 — identity document validation technology (IDVT).
// Our DIGITAL verification. ONE photographic document, whose cryptographic and
// physical security features the technology validates.
// ---------------------------------------------------------------------------
export const IDVT_DOCUMENTS = [
  // "From any country" is stated explicitly because leaving it implicit cost us
  // a customer: the checker was asked about a Jordanian passport and answered
  // that it "is not on the fixed list of accepted documents", having read the
  // country limits on the driving licence and identity card entries below and
  // generalised them to passports. There is no list of approved passport
  // countries. The only questions are biometric/machine-readable, and in date.
  //
  // ONE exception, and it is ours, not Companies House's: our automated check
  // cannot read Iranian passports (an Iranian passport holder's digital check
  // failed in Sept 2026 and had to be moved to a video call). The standard
  // would allow it; the technology we use for this route does not. Stated on
  // the entry itself so the page, the emails, the assistants and the checker
  // all carry it. See DIGITAL_PASSPORT_EXCEPTIONS.
  { name: 'Biometric or machine-readable passport from any country, or Irish passport card', note: 'except Iranian passports, which our automated check cannot read — use the video call' },
  { name: 'Photocard driving licence from the UK, Channel Islands, Isle of Man or EU', note: 'full or provisional' },
  { name: 'Biometric identity card from the EU, Norway, Iceland or Liechtenstein' },
  { name: 'UK biometric residence permit (BRP)' },
  { name: 'UK biometric residence card (BRC)' },
  { name: 'UK Frontier Worker permit' },
  { name: 'eVisa' },
];

// Passports our automated check cannot read, although the Companies House
// standard would accept them. A person CAN check them, so they are Group A on
// the video call and in person as normal. Add a country here only on evidence
// of real failures, never on assumption.
export const DIGITAL_PASSPORT_EXCEPTIONS = [
  { country: 'Iran', adjective: 'Iranian', match: /\b(iran|iranian|persian)\b/i },
];

export const DIGITAL_PASSPORT_EXCEPTION_NOTE =
  'Iranian passports are the one exception: our automated check cannot read them, so please use the ' +
  'video call (£49) or come in person (£59), where a qualified person examines your passport with you.';

// Does this free-text description name a passport our automated check cannot read?
export function isDigitalExceptionPassport(text) {
  const t = String(text || '');
  return /passport/i.test(t) && DIGITAL_PASSPORT_EXCEPTIONS.some((e) => e.match.test(t));
}

// ---------------------------------------------------------------------------
// OPTION 2 — a person checks the documents, remotely or in person.
// Our VIDEO CALL and IN-PERSON verification. TWO documents: either two from
// Group A, or one from Group A and one from Group B.
// ---------------------------------------------------------------------------
export const GROUP_A = [
  { name: 'Passport, or Irish passport card' },
  { name: 'Biometric identity card from the EU, Norway, Iceland or Liechtenstein' },
  { name: 'UK biometric residence permit (BRP)' },
  { name: 'UK biometric residence card (BRC)' },
  { name: 'UK accredited PASS card' },
  { name: 'UK or EU driver digital tachograph card' },
  { name: 'Photocard driving licence from the UK, Channel Islands, Isle of Man or EU' },
  { name: 'UK HM Forces ID card, or HM Armed Forces Veteran Card' },
  { name: 'UK Frontier Worker permit' },
  { name: 'Government-issued photographic work permit, immigration document or visa' },
  { name: 'Firearms licence from the UK, Channel Islands or Isle of Man' },
  { name: 'Any other photographic identity document listed on PRADO', note: 'this option only — not available on the digital check' },
];

// Group B accepted whatever address they show.
export const GROUP_B = [
  { name: 'Birth or adoption certificate' },
  { name: 'Marriage or civil partnership certificate' },
  { name: 'Non-photographic immigration document, visa or work permit' },
  { name: 'Bank or building society statement' },
];

// Group B accepted ONLY where they show the person's current address.
export const GROUP_B_CURRENT_ADDRESS = [
  { name: 'Council tax statement' },
  { name: 'Utility bill' },
  { name: 'Mortgage statement' },
  { name: 'Insurance policy' },
  { name: 'Local authority or social housing rental agreement' },
];

// Rules that apply to BOTH options.
export const DOCUMENT_RULES = {
  // FIRM POLICY, STRICTER THAN THE STANDARD. Companies House permits a passport
  // up to 6 months past expiry on the digital check and 18 months on the
  // person-checked routes, and a BRP up to 18 months. We do not use any of that
  // tolerance. Every document must be in date.
  //
  // That is a deliberate decision by the MLRO, not an oversight: the CH standard
  // is a floor, not a ceiling, and an expired document is weaker evidence for our
  // own customer due diligence under MLR 2017 than it is for the Registrar. A
  // firm may hold a higher documentary standard than the minimum, and this one
  // does. Do not reintroduce the tolerances because the official guidance allows
  // them — the gap is intentional.
  expiry: 'Every document must be in date. We cannot accept one that has expired, been cancelled, or been replaced by a newer one — for example a passport with a cut-off corner. This is stricter than the Companies House minimum and it is our own policy.',
  nonUkResidents: 'Anyone who does not live in the UK must provide at least one government-issued document.',
  twoFromGroupA: 'You can bring two Group A documents instead of one Group A and one Group B.',
  // 7 years, not the 5 that the money-laundering rules require for customer
  // due diligence. Both duties apply; 7 is the longer one and the one that
  // governs these records, so it is what customers are told.
  retentionYears: 7,
  retention: 'We keep copies of the documents and records of our checks for 7 years, as Companies House requires.',
  // Recorded and submitted for Group A / IDVT documents only.
  whatWeSubmit: 'When we confirm your verification to Companies House we give each photographic document\'s reference number, expiry date and country of issue.',
};

export const OFFICIAL_GUIDANCE_URL =
  'https://www.gov.uk/guidance/how-to-meet-companies-house-identity-verification-standard';

// Render helper — "Name (note)" — so every surface formats identically.
export const documentLine = (d) => (d.note ? `${d.name} (${d.note})` : d.name);
export const documentNames = (list) => list.map(documentLine);

// ---------------------------------------------------------------------------
// DOCUMENTS CUSTOMERS NAME BY THEIR LOCAL NAME
//
// The lists above are written in Companies House's words. Customers do not
// use them. A month of enquiries (Sep–Oct 2026) arrived as "my CNIC", "can I
// use Aadhaar", "I only have my Emirates ID", "a kimlik card", "a PASS card
// from the Post Office", "my tacho card" — and whether an assistant answered
// correctly depended on whether the model happened to know what each one is.
// This is the translation table. Every AI surface renders it, so the answer
// to "can I use X" is the same in web chat, on WhatsApp, on the phone and in
// the free-text checker, and it is the answer the landing pages give.
//
//   digital       — usable on the automated check
//   personChecked — 'A' (Group A photo ID), 'B' (Group B supporting), or false
//   use           — the one-sentence answer, in the words we want used
// ---------------------------------------------------------------------------
export const NAMED_DOCUMENTS = [
  {
    term: 'CNIC or NICOP (Pakistan National Identity Card)',
    digital: false, personChecked: 'A',
    use: 'Not on the automated check, which accepts identity cards only from the EU, Norway, Iceland and Liechtenstein. Accepted on the video call or in person as Group A photo ID — Companies House names the Pakistani National Identity Card as an example of a PRADO document. If they also hold a Pakistani passport, the £39 digital check is simpler.',
  },
  {
    term: 'Aadhaar card, PAN card or Indian voter ID',
    digital: false, personChecked: false,
    use: 'Not accepted on any route. None of them is a photographic identity document in the sense Companies House means, and we do not rely on them on a video call either. The document for an Indian director is the passport, which works on the digital check.',
  },
  {
    term: 'Emirates ID (UAE)',
    digital: false, personChecked: false,
    use: 'Not on the automated check. Use the passport instead — whatever country issued it, it works on the £39 digital check. Do not start the check with the Emirates ID; it will fail and they will have to begin again.',
  },
  {
    term: 'Turkish identity card (T.C. Kimlik Kartı)',
    digital: false, personChecked: 'A',
    use: 'Not on the automated check — it is biometric, but Türkiye is not in the EU and the automated check accepts identity cards only from the EU, Norway, Iceland and Liechtenstein. Accepted on the video call or in person as Group A photo ID (it is on PRADO). A Turkish passport works on the digital check.',
  },
  {
    term: 'Turkish driving licence',
    digital: false, personChecked: 'A',
    use: 'Video call or in person only. The automated check accepts photocard driving licences from the UK, Channel Islands, Isle of Man and EU, and Türkiye is not in the EU. On the person-checked routes it is Group A photo ID.',
  },
  {
    term: 'Post Office PASS card (UK accredited PASS card)',
    digital: false, personChecked: 'A',
    use: 'Video call or in person only, as Group A photo ID, alongside one supporting document. Not on the automated check. It is the quickest photo ID for someone who has none: a few pounds, usually issued within about a week.',
  },
  {
    term: 'Driver tachograph card (UK or EU driver digital tachograph card)',
    digital: false, personChecked: 'A',
    use: 'Video call or in person only, as Group A photo ID. It has its own entry on the Companies House list — record it as a tachograph card, not as a driving licence. It carries no address, so it is not a proof of address; pair it with a bank statement or a bill.',
  },
  {
    term: 'Provisional UK driving licence',
    digital: true, personChecked: 'A',
    use: 'Accepted on the £39 digital check on its own, full or provisional, and on the person-checked routes as Group A. For someone with no photo ID at all, applying for one is often the fastest fix.',
  },
  {
    term: 'EU national identity card (German Personalausweis, Italian carta d\'identità, Spanish DNI, and so on)',
    digital: true, personChecked: 'A',
    use: 'Accepted on the £39 digital check — a biometric identity card from the EU, Norway, Iceland or Liechtenstein — and as Group A on the person-checked routes. The address sticker on the back of a German card is normal, not tampering.',
  },
  {
    term: 'HMRC letter, P60, payslip, National Insurance card or NHS card',
    digital: false, personChecked: false,
    use: 'Not accepted on any route — not even as a Group B supporting document. Group B is birth, adoption, marriage or civil partnership certificates, non-photographic immigration documents, bank statements, and bills or statements at the current address.',
  },
  {
    term: 'Birth certificate',
    digital: false, personChecked: 'B',
    use: 'Group B only. It supports a photographic document on the video call or in person; it can never be the only document, because every route needs one photographic identity document.',
  },
  {
    term: 'Iranian passport',
    digital: false, personChecked: 'A',
    use: 'Not on the automated check — our technology cannot read Iranian passports, even biometric ones. Accepted on the video call or in person as Group A photo ID, alongside one supporting document (a proof of current address, or a second photo ID). Book the video call; do not start the £39 check with it.',
  },
  {
    term: 'Iranian driving licence (گواهینامه رانندگی)',
    digital: false, personChecked: 'A',
    use: 'Video call or in person only, as Group A photo ID. Iranian photocard driving licences are listed on PRADO (IRN-FO-01001 and IRN-FO-02001). With an Iranian passport it makes two photo IDs, so no proof of address is needed.',
  },
  {
    term: 'Iranian national identity card (Kart-e Melli / کارت ملی)',
    digital: false, personChecked: false,
    use: 'Not accepted on any route. It is not listed on PRADO, so there is no reference to check it against. An Iranian passport plus an Iranian driving licence is the usual pair for the video call.',
  },
  {
    term: 'Expired passport',
    digital: false, personChecked: false,
    use: 'No, on every route. Firm policy is that every document is in date; we do not use the grace period Companies House allows. Renew it first.',
  },
];

export const NO_PHOTO_ID_GUIDANCE =
  'Someone with no photographic identity document at all cannot be verified yet by anyone — at least one is required on every route, and a bank statement, birth certificate and P60 together do not replace it. Say so before they pay. The quickest fixes are a Post Office PASS card (about a week) or a provisional UK driving licence; once they hold one, the video call takes it alongside a supporting document.';

export const namedDocumentLine = (d) => `- ${d.term}: ${d.use}`;

// ---------------------------------------------------------------------------
// THE DOCUMENT CHECKER
//
// The old checker offered "UK Passport", "EU/EEA Passport", "UK Driving
// Licence", "EU Driving Licence" and "Other Government Photo ID". A Turkish
// director with a Turkish driving licence could not describe his document in
// it at all — so the one tool on the page built to answer his question was
// silent, and he emailed instead, asking us to confirm in writing.
//
// These options are written around the decisions that actually differ, which
// are mostly about WHERE a document was issued, and they carry the reason with
// the answer. `digitalNote` exists because "not available" without a reason
// reads as a fault in the customer rather than a rule.
//
//   idvt    — usable on the digital check (one document, no proof of address)
//   personA — counts as Group A photo ID when a person checks (video/in-person)
//   personB — counts as a Group B supporting document
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// THE NEXT STEP
//
// Whatever the checker concludes, the customer gets exactly ONE thing to click.
// Not three prices to weigh up, not a scroll back to the route sections — one
// button that does the thing their answer implies. Offering all three routes
// after telling someone which one they can use makes them do the reasoning
// twice, and the second time is the one where they close the tab.
//
// The keys are the `route` values the document advisor returns. Both the
// free-text checker and the picker resolve to one of these, and a test asserts
// the two sets stay identical — a route with no button leaves someone who has
// just been told "yes" with nothing to press.
//
// EVERY BUY BUTTON GOES TO /customer/checkout, NEVER /customer/login.
// We take payment before we ask anyone to make an account. /customer/checkout
// is the guest path: email, name, phone, pay — the silent account is created
// at the point of payment and the password comes afterwards. Sending a buyer
// to /customer/login instead does not even gate anything, because login lands
// them on the same checkout; it just inserts a sign-up (and a profile form, if
// their profile is incomplete) between a customer who has decided to buy and
// the card form. See src/lib/checkoutLinks.test.js, which fails the build if a
// buy button points at the login wall again.
// ---------------------------------------------------------------------------
export const NEXT_STEPS = {
  digital: {
    label: 'Start digital verification — £39',
    to: '/customer/checkout?plan=digital',
    blurb: 'Five minutes on your phone. Most people have their personal code the same day.',
  },
  video_call: {
    label: 'Book your video call — £49',
    to: '/customer/checkout?plan=video-call',
    blurb: 'A qualified accountant checks your documents live. Appointments usually within 24 hours.',
  },
  in_person: {
    label: 'Book an in-person appointment — £59',
    to: '/customer/checkout?plan=in-person',
    blurb: 'Manchester or Ashton-under-Lyne. Bring the original documents.',
  },
  contact_us: {
    label: 'Talk to us — 0161 317 8556',
    href: 'tel:01613178556',
    blurb: 'We would much rather answer a question than take money for the wrong service.',
  },
};

export const VERIFICATION_ROUTES = Object.keys(NEXT_STEPS);

export const DOCUMENT_CHECKER_OPTIONS = [
  {
    id: 'passport-biometric', label: 'Passport — biometric or machine-readable (any country except Iran)',
    idvt: true, personA: true,
    note: 'Must be in date.',
  },
  {
    id: 'passport-iran', label: 'Passport — Iranian',
    idvt: false, personA: true,
    digitalNote: 'Our automated check cannot read Iranian passports, even biometric ones. A qualified person can check it by video call or in person.',
  },
  {
    id: 'passport-old', label: 'Passport — older, without a machine-readable strip',
    idvt: false, personA: true,
    digitalNote: 'The digital check reads the machine-readable strip, so an older passport cannot be used for it. A person can check it by video call.',
  },
  {
    id: 'licence-uk-eu', label: 'Driving licence — UK, Channel Islands, Isle of Man or EU',
    idvt: true, personA: true, note: 'Full or provisional, photocard.',
  },
  {
    id: 'licence-other', label: 'Driving licence — issued anywhere else (e.g. Türkiye, USA, India)',
    idvt: false, personA: true,
    digitalNote: 'The digital check only accepts driving licences from the UK, Channel Islands, Isle of Man or EU. Yours is not on that list.',
    personNote: 'Accepted by video call or in person if your licence is catalogued on PRADO, which most national licences are. Ask us if you are unsure.',
  },
  {
    id: 'id-card-eu', label: 'National identity card — EU, Norway, Iceland or Liechtenstein',
    idvt: true, personA: true, note: 'Must have a biometric chip.',
  },
  {
    id: 'id-card-other', label: 'National identity card — issued anywhere else',
    idvt: false, personA: true,
    digitalNote: 'The digital check accepts identity cards from the EU, Norway, Iceland and Liechtenstein only.',
    personNote: 'Accepted by video call or in person if your card is catalogued on PRADO, which most national identity cards are. Not the Iranian national identity card — it is not on PRADO.',
  },
  { id: 'brp', label: 'UK biometric residence permit (BRP)', idvt: true, personA: true, note: 'Must be in date.' },
  { id: 'brc', label: 'UK biometric residence card (BRC)', idvt: true, personA: true },
  { id: 'frontier', label: 'UK Frontier Worker permit', idvt: true, personA: true },
  {
    id: 'evisa', label: 'eVisa', idvt: true, personA: false,
    personNote: 'An eVisa is read by the digital check. For video call or in person you would need a physical photographic document instead.',
  },
  { id: 'pass-card', label: 'UK accredited PASS card', idvt: false, personA: true, digitalNote: 'Not on the digital check’s list, but fine by video call or in person.' },
  { id: 'tachograph', label: 'UK or EU driver digital tachograph card', idvt: false, personA: true, digitalNote: 'Not on the digital check’s list, but fine by video call or in person.' },
  { id: 'forces', label: 'UK HM Forces ID card or Armed Forces Veteran Card', idvt: false, personA: true, digitalNote: 'Not on the digital check’s list, but fine by video call or in person.' },
  { id: 'work-permit-photo', label: 'Government-issued work permit, immigration document or visa — with a photo', idvt: false, personA: true, digitalNote: 'Not on the digital check’s list, but fine by video call or in person.' },
  { id: 'firearms', label: 'Firearms licence — UK, Channel Islands or Isle of Man', idvt: false, personA: true, digitalNote: 'Not on the digital check’s list, but fine by video call or in person.' },
  {
    id: 'prado-other', label: 'Another government photo ID listed on PRADO',
    idvt: false, personA: true,
    digitalNote: 'The digital check does not use PRADO — it reads a fixed list of document types.',
    personNote: 'This is exactly what the video call and in-person routes are for.',
  },
  // The supporting documents. Each carries a digitalNote of its own: someone
  // who picks one of these and sees the digital check greyed out with no
  // reason concludes we cannot verify them, when in fact the digital check
  // simply never asks for proof of address.
  {
    id: 'bank-statement', label: 'Bank or building society statement',
    idvt: false, personA: false, personB: true,
    digitalNote: 'The digital check needs no proof of address at all — just one photo ID. Pick your photo ID above to see whether it qualifies.',
  },
  {
    id: 'certificate', label: 'Birth, adoption, marriage or civil partnership certificate',
    idvt: false, personA: false, personB: true,
    digitalNote: 'The digital check reads one photographic document. Pick your photo ID above to see whether it qualifies.',
  },
  {
    id: 'immigration-nophoto', label: 'Immigration document, visa or work permit — without a photo',
    idvt: false, personA: false, personB: true,
    digitalNote: 'The digital check reads one photographic document. Pick your photo ID above to see whether it qualifies.',
  },
  {
    id: 'utility-bill', label: 'Utility bill, council tax, mortgage statement, insurance policy or rental agreement',
    idvt: false, personA: false, personB: true, note: 'Must show your current address.',
    digitalNote: 'The digital check needs no proof of address at all — just one photo ID. Pick your photo ID above to see whether it qualifies.',
  },
];
